import { useSeoMeta } from '@unhead/react';
import { useState, useEffect } from 'react';
import { BitcoinPrice, fetchBitcoinPrice, usdToSats, formatSats } from '@/lib/bitcoin';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Zap, Package, Bitcoin, Copy, Check } from 'lucide-react';
import { useToast } from '@/hooks/useToast';
import { QRCodeCanvas } from 'qrcode.react';

const Index = () => {
  useSeoMeta({
    title: 'Simply Tallow - 100% Grass-Fed Beef Tallow Balm',
    description: 'Pure. Simple. Natural. Heal your skin with our grass-fed tallow balm.',
  });

  const [btcPrice, setBtcPrice] = useState<BitcoinPrice>({ btcusd: 90000, satsPerDollar: 1111 });
  const [selectedType, setSelectedType] = useState<'whipped' | 'natural'>('whipped');
  const [quantity, setQuantity] = useState(1);
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [copied, setCopied] = useState(false);
  const [shippingInfo, setShippingInfo] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    npub: ''
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchBitcoinPrice().then(setBtcPrice);
    const interval = setInterval(() => {
      fetchBitcoinPrice().then(setBtcPrice);
    }, 60000); // Update every minute
    return () => clearInterval(interval);
  }, []);

  const baseProductPrice = 21.00;

  // Simple pricing - no discount
  const productTotal = baseProductPrice * quantity;

  const baseShippingPrice = 7.50;
  const shippingPrice = baseShippingPrice + (baseShippingPrice * 0.33 * (quantity - 1));
  const subtotal = productTotal + shippingPrice;
  const discount = couponApplied ? subtotal * 0.05 : 0;
  const total = subtotal - discount;

  const productTotalSats = usdToSats(productTotal, btcPrice.satsPerDollar);
  const shippingPriceSats = usdToSats(shippingPrice, btcPrice.satsPerDollar);
  const totalSats = usdToSats(total, btcPrice.satsPerDollar);
  const baseProductPriceSats = usdToSats(baseProductPrice, btcPrice.satsPerDollar);

  const applyCoupon = () => {
    if (couponCode.toUpperCase() === 'SATOSHI' && !couponApplied) {
      setCouponApplied(true);
      toast({
        title: "Coupon Applied!",
        description: "5% discount has been applied to your order",
      });
    } else if (couponApplied) {
      toast({
        title: "Coupon Already Applied",
        description: "You can only use the coupon once per order",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Invalid Coupon",
        description: "Please enter a valid coupon code",
        variant: "destructive",
      });
    }
  };

  const handleCheckout = () => {
    setShowCheckout(true);
  };

  const handleSaveShipping = () => {
    // Validate required fields
    if (!shippingInfo.name && !shippingInfo.npub) {
      toast({
        title: "Missing Information",
        description: "Please provide either a name or Nostr pubkey",
        variant: "destructive",
      });
      return;
    }

    if (!shippingInfo.address || !shippingInfo.city || !shippingInfo.state || !shippingInfo.zip || !shippingInfo.country) {
      toast({
        title: "Missing Information",
        description: "Please fill in all shipping address fields",
        variant: "destructive",
      });
      return;
    }

    // Validate ZIP/Postal code format
    const usZipRegex = /^\d{5}(-\d{4})?$/;
    const canadaPostalRegex = /^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/;

    if (!usZipRegex.test(shippingInfo.zip) && !canadaPostalRegex.test(shippingInfo.zip)) {
      toast({
        title: "Invalid ZIP/Postal Code",
        description: "Please enter a valid US ZIP code (12345) or Canadian postal code (K1A 0B1)",
        variant: "destructive",
      });
      return;
    }

    // Show QR code
    setShowQR(true);

    // Log the order info (in production, this would be sent to a server)
    console.log('Order Details:', {
      shipping: shippingInfo,
      product: selectedType,
      quantity,
      totalSats,
      totalUSD: total.toFixed(2)
    });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast({
      title: "Copied!",
      description: "Lightning invoice copied to clipboard",
    });
  };

  return (
    <div className="min-h-screen bg-white text-black relative">
      {/* Pixelated pasture background pattern */}
      <div className="fixed inset-0 opacity-5 pointer-events-none" style={{
        backgroundImage: `
          repeating-linear-gradient(45deg,
            #4ade80 0px, #4ade80 16px,
            #22c55e 16px, #22c55e 32px,
            #16a34a 32px, #16a34a 48px,
            #15803d 48px, #15803d 64px
          ),
          repeating-linear-gradient(-45deg,
            transparent 0px, transparent 32px,
            #0000000d 32px, #0000000d 64px
          )
        `,
        backgroundSize: '64px 64px',
        imageRendering: 'pixelated'
      }} />

      {/* Header with logo */}
      <header className="border-b-4 border-black p-2 sm:p-4">
        <div className="container mx-auto">
          <div className="flex flex-col items-center">
            <img
              src="/main-logo.jpg"
              alt="Simply Tallow"
              className="w-20 h-20 sm:w-32 sm:h-32 mb-2 sm:mb-4 pixelated"
              style={{ imageRendering: 'auto' }}
            />
            <h1 className="font-pixel text-lg sm:text-2xl md:text-3xl text-center tracking-wider">
              SIMPLY TALLOW
            </h1>
            <p className="text-center mt-1 sm:mt-2 text-xs sm:text-sm font-mono">[ 100% GRASS-FED BEEF TALLOW BALM ]</p>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="container mx-auto px-2 sm:px-4 py-4 sm:py-8 max-w-4xl relative z-10">
        {/* Product Section */}
        <div className="grid md:grid-cols-2 gap-4 sm:gap-8 mb-8 sm:mb-12">
          {/* Product Images */}
          <div className="space-y-4">
            <div className="border-4 border-black p-2 bg-white">
              <img
                src="/tallow-balm-pyramid-image.jpeg"
                alt="Tallow Balm"
                className="w-full h-auto"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="border-2 border-black p-1">
                <img
                  src="/tallow-balm-openlid-image.jpeg"
                  alt="Open Tallow Balm"
                  className="w-full h-auto"
                />
              </div>
              <div className="border-2 border-black p-1">
                <img
                  src="/tallow-balm-inhand-image.jpeg"
                  alt="Tallow Balm in Hand"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>

          {/* Product Info & Cart */}
          <div className="space-y-6">
            <Card className="border-4 border-black rounded-none p-3 sm:p-6 bg-white">
              <h2 className="font-pixel text-sm sm:text-lg mb-3 sm:mb-4">TALLOW BALM</h2>

              {/* Product Info */}
              <div className="mb-4 p-3 border-2 border-black bg-gray-50">
                <div className="space-y-2 font-mono text-xs">
                  <p>
                    <span className="font-bold">INGREDIENTS:</span><br/>
                    Grass-Fed Beef Tallow, Organic Extra Virgin Olive Oil
                  </p>
                  <p>
                    <span className="font-bold">SIZE:</span> 4oz
                  </p>
                  <p>
                    <span className="font-bold">QUALITY:</span> 100% Natural, No Additives
                  </p>
                </div>
              </div>

              {/* Price Display */}
              <div className="mb-4 p-3 bg-black text-white">
                <div className="flex items-center justify-between">
                  <span className="font-mono">PRICE:</span>
                  <div className="text-right">
                    <div className="font-mono text-lg">
                      <Bitcoin className="inline w-4 h-4 mr-1" />
                      {formatSats(baseProductPriceSats)} SATS
                    </div>
                    <div className="text-sm opacity-75">($21.00 USD)</div>
                  </div>
                </div>
              </div>

              {/* Type Selection */}
              <div className="mb-4">
                <label className="font-mono text-sm mb-2 block">SELECT TYPE:</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setSelectedType('whipped')}
                    className={`border-2 p-2 font-mono text-sm transition-all ${
                      selectedType === 'whipped'
                        ? 'border-black bg-black text-white'
                        : 'border-black hover:bg-gray-100'
                    }`}
                  >
                    WHIPPED
                  </button>
                  <button
                    onClick={() => setSelectedType('natural')}
                    className={`border-2 p-2 font-mono text-sm transition-all ${
                      selectedType === 'natural'
                        ? 'border-black bg-black text-white'
                        : 'border-black hover:bg-gray-100'
                    }`}
                  >
                    NATURAL
                  </button>
                </div>
              </div>

              {/* Quantity */}
              <div className="mb-4">
                <label className="font-mono text-sm mb-2 block">QUANTITY:</label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="border-2 border-black w-8 h-8 flex items-center justify-center hover:bg-black hover:text-white transition-colors"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    className="border-2 border-black w-16 text-center font-mono"
                    min="1"
                  />
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="border-2 border-black w-8 h-8 flex items-center justify-center hover:bg-black hover:text-white transition-colors"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Coupon */}
              <div className="mb-4">
                <label className="font-mono text-sm mb-2 block">COUPON CODE:</label>
                <div className="flex gap-2">
                  <Input
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    placeholder="Enter code"
                    className="border-2 border-black rounded-none font-mono"
                  />
                  <Button
                    onClick={applyCoupon}
                    variant="outline"
                    className="border-2 border-black rounded-none font-mono hover:bg-black hover:text-white"
                  >
                    APPLY
                  </Button>
                </div>
                {couponApplied && (
                  <Badge className="mt-2 bg-black text-white rounded-none">5% OFF APPLIED</Badge>
                )}
              </div>

              {/* Order Summary */}
              <div className="border-t-2 border-black pt-4 space-y-2 font-mono text-sm">
                <div className="flex justify-between">
                  <span>Product ({quantity}x):</span>
                  <span>{formatSats(productTotalSats)} sats</span>
                </div>
                <div className="flex justify-between">
                  <span>Shipping{quantity > 1 ? ` (${quantity} jars)` : ''}:</span>
                  <span>{formatSats(shippingPriceSats)} sats</span>
                </div>
                {couponApplied && (
                  <div className="flex justify-between text-green-600">
                    <span>Discount (5%):</span>
                    <span>-{formatSats(usdToSats(discount, btcPrice.satsPerDollar))} sats</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg border-t-2 border-black pt-2">
                  <span>TOTAL:</span>
                  <div className="text-right">
                    <div>{formatSats(totalSats)} SATS</div>
                    <div className="text-sm font-normal">(${total.toFixed(2)} USD)</div>
                  </div>
                </div>
              </div>

              {/* Checkout Button */}
              <Button
                onClick={handleCheckout}
                className="w-full mt-4 sm:mt-6 bg-black text-white rounded-none border-2 border-black hover:bg-white hover:text-black transition-all font-mono py-4 sm:py-6 text-base sm:text-lg"
              >
                <Zap className="mr-2 w-4 h-4 sm:w-5 sm:h-5" />
                PAY WITH LIGHTNING
              </Button>
              <p className="text-xs text-center mt-2 font-mono">
                SimplyTallow@coinos.io
              </p>
            </Card>
          </div>
        </div>

        {/* Our Story */}
        <Card className="border-4 border-black rounded-none p-8 mb-12 bg-white">
          <h2 className="font-pixel text-xl mb-6 text-center">OUR STORY</h2>
          <div className="space-y-4 font-mono text-sm leading-relaxed max-w-2xl mx-auto">
            <p>
              Simply Tallow was born from a mother's desperate search to heal her son's severe eczema.
              We discovered for ourselves the ancient wisdom of tallow-based skincare.
            </p>
            <p>
              Every jar is still made by hand, with the same care and attention, as we use it in our own household.
            </p>
            <p>
              We source our tallow exclusively from local, regenerative farms where cattle graze freely on
              organic pastures. This isn't just about skincare – it's about honoring traditional practices,
              supporting sustainable agriculture, and nurturing your skin the way nature intended, and being
              free of the endless list of chemicals and additives.
            </p>
          </div>
        </Card>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-3 gap-4 mb-12">
          {[
            { icon: '🐄', title: '100% GRASS-FED', desc: 'Premium beef tallow' },
            { icon: '🫒', title: 'ORGANIC OLIVE OIL', desc: 'Extra virgin quality' },
            { icon: '✋', title: 'HANDCRAFTED', desc: '4oz jar, small batch' },
          ].map((benefit, i) => (
            <Card key={i} className="border-2 border-black rounded-none p-4 text-center bg-white">
              <div className="text-3xl mb-2">{benefit.icon}</div>
              <h3 className="font-pixel text-xs mb-1">{benefit.title}</h3>
              <p className="font-mono text-xs">{benefit.desc}</p>
            </Card>
          ))}
        </div>

        {/* Contact Section */}
        <Card className="border-4 border-black rounded-none p-6 bg-black text-white">
          <h2 className="font-pixel text-lg mb-4 text-center">CONTACT</h2>
          <div className="space-y-3 font-mono text-sm">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
              <span className="opacity-75">EMAIL:</span>
              <a href="mailto:simply.tallow.contact@protonmail.com" className="hover:underline break-all">
                simply.tallow.contact@protonmail.com
              </a>
            </div>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
              <span className="opacity-75">NOSTR:</span>
              <a
                href="/npub1vj0faqry50n9ugsrtaprfuxd0n2actly5mvu6ct7v50ajrr63s5s8zwucj"
                className="hover:underline break-all"
              >
                npub1vj0faqry50n9ugsrtaprfuxd0n2actly5mvu6ct7v50ajrr63s5s8zwucj
              </a>
            </div>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
              <span className="opacity-75">LIGHTNING:</span>
              <span className="break-all">SimplyTallow@coinos.io</span>
            </div>
          </div>
        </Card>

        {/* MKStack attribution */}
        <div className="text-center mt-12 pb-8">
          <a
            href="https://soapbox.pub/mkstack"
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-xs hover:underline"
          >
            [ Vibed with MKStack ]
          </a>
        </div>
      </main>

      {/* Checkout Dialog */}
      <Dialog open={showCheckout} onOpenChange={setShowCheckout}>
        <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto border-4 border-black rounded-none bg-white p-4 md:p-6">
          <DialogHeader>
            <DialogTitle className="font-pixel text-sm sm:text-lg md:text-xl">
              {!showQR ? 'SHIPPING INFORMATION' : 'PAY WITH LIGHTNING'}
            </DialogTitle>
          </DialogHeader>

          {!showQR ? (
            <div className="space-y-3 sm:space-y-4">
              {/* Name or Npub */}
              <div className="space-y-2">
                <Label className="font-mono text-sm">NAME OR NOSTR PUBKEY (NPUB):</Label>
                <Input
                  placeholder="John Doe or npub1..."
                  value={shippingInfo.name || shippingInfo.npub}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.startsWith('npub')) {
                      setShippingInfo({...shippingInfo, npub: value, name: ''});
                    } else {
                      setShippingInfo({...shippingInfo, name: value, npub: ''});
                    }
                  }}
                  className="border-2 border-black rounded-none font-mono"
                />
              </div>

              {/* Address */}
              <div className="space-y-2">
                <Label className="font-mono text-sm">STREET ADDRESS OR PO BOX:</Label>
                <Textarea
                  placeholder="123 Main St or PO Box 123"
                  value={shippingInfo.address}
                  onChange={(e) => setShippingInfo({...shippingInfo, address: e.target.value})}
                  className="border-2 border-black rounded-none font-mono"
                  rows={2}
                />
              </div>

              {/* City, State, Zip */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                <div className="space-y-2">
                  <Label className="font-mono text-xs">CITY:</Label>
                  <Input
                    placeholder="Austin"
                    value={shippingInfo.city}
                    onChange={(e) => setShippingInfo({...shippingInfo, city: e.target.value})}
                    className="border-2 border-black rounded-none font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-mono text-xs">STATE/PROV:</Label>
                  <Input
                    placeholder="TX or ON"
                    value={shippingInfo.state}
                    onChange={(e) => setShippingInfo({...shippingInfo, state: e.target.value.toUpperCase()})}
                    className="border-2 border-black rounded-none font-mono"
                    maxLength={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-mono text-xs">ZIP/POSTAL:</Label>
                  <Input
                    placeholder="78701 or K1A 0B1"
                    value={shippingInfo.zip}
                    onChange={(e) => setShippingInfo({...shippingInfo, zip: e.target.value.toUpperCase()})}
                    className="border-2 border-black rounded-none font-mono"
                    maxLength={10}
                  />
                </div>
              </div>

              {/* Country */}
              <div className="space-y-2">
                <Label className="font-mono text-sm">COUNTRY:</Label>
                <Input
                  placeholder="USA or Canada"
                  value={shippingInfo.country}
                  onChange={(e) => setShippingInfo({...shippingInfo, country: e.target.value.toUpperCase()})}
                  className="border-2 border-black rounded-none font-mono"
                />
              </div>

              {/* Order Summary */}
              <div className="border-2 border-black p-4 bg-gray-50">
                <h3 className="font-pixel text-sm mb-2">ORDER SUMMARY:</h3>
                <div className="space-y-1 font-mono text-xs">
                  <div className="flex justify-between">
                    <span>{quantity}x {selectedType.toUpperCase()} TALLOW BALM</span>
                    <span>{formatSats(productTotalSats)} sats</span>
                  </div>
                  <div className="flex justify-between">
                    <span>SHIPPING:</span>
                    <span>{formatSats(shippingPriceSats)} sats</span>
                  </div>
                  {couponApplied && (
                    <div className="flex justify-between text-green-600">
                      <span>DISCOUNT (5%):</span>
                      <span>-{formatSats(usdToSats(discount, btcPrice.satsPerDollar))} sats</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold pt-2 border-t border-black">
                    <span>TOTAL:</span>
                    <span>{formatSats(totalSats)} SATS (${total.toFixed(2)})</span>
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <Button
                onClick={handleSaveShipping}
                className="w-full bg-black text-white rounded-none border-2 border-black hover:bg-white hover:text-black transition-all font-mono py-4"
              >
                SAVE & GENERATE INVOICE
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Lightning Invoice */}
              <div className="text-center space-y-4">
                {/* QR Code */}
                <div className="flex justify-center p-2 sm:p-4 bg-white border-4 border-black">
                  <QRCodeCanvas
                    value={`lightning:SimplyTallow@coinos.io?amount=${totalSats}&label=Simply%20Tallow%20Order`}
                    size={window.innerWidth < 640 ? 200 : 256}
                    level="M"
                    includeMargin={false}
                  />
                </div>

                {/* Invoice Amount */}
                <div className="bg-black text-white p-4">
                  <p className="font-pixel text-lg mb-2">SEND EXACTLY:</p>
                  <p className="font-mono text-2xl">{formatSats(totalSats)} SATS</p>
                  <p className="font-mono text-sm opacity-75">(${total.toFixed(2)} USD)</p>
                </div>

                {/* Lightning Address */}
                <div className="border-2 border-black p-4 bg-gray-50">
                  <p className="font-mono text-xs mb-2">LIGHTNING ADDRESS:</p>
                  <div className="flex items-center justify-center gap-2">
                    <code className="font-mono text-sm break-all">SimplyTallow@coinos.io</code>
                    <Button
                      onClick={() => copyToClipboard('SimplyTallow@coinos.io')}
                      variant="ghost"
                      size="icon"
                      className="border border-black rounded-none hover:bg-black hover:text-white"
                    >
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>

                {/* Instructions */}
                <div className="text-left space-y-2 font-mono text-xs">
                  <p className="font-bold">INSTRUCTIONS:</p>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Scan QR code or copy lightning address</li>
                    <li>Send EXACT amount: {formatSats(totalSats)} sats</li>
                    <li>Include order note: "{shippingInfo.name || shippingInfo.npub}"</li>
                    <li>Email confirmation to: simply.tallow.contact@protonmail.com</li>
                  </ol>
                </div>

                {/* Shipping Info Summary */}
                <div className="border-t-2 border-black pt-4 text-left">
                  <p className="font-pixel text-sm mb-2">SHIPPING TO:</p>
                  <div className="font-mono text-xs space-y-1">
                    <p>{shippingInfo.name || shippingInfo.npub}</p>
                    <p>{shippingInfo.address}</p>
                    <p>{shippingInfo.city}, {shippingInfo.state} {shippingInfo.zip}</p>
                    <p>{shippingInfo.country}</p>
                  </div>
                </div>

                {/* Close Button */}
                <Button
                  onClick={() => {
                    setShowCheckout(false);
                    setShowQR(false);
                    // Reset form
                    setShippingInfo({
                      name: '',
                      address: '',
                      city: '',
                      state: '',
                      zip: '',
                      country: '',
                      npub: ''
                    });
                  }}
                  className="w-full bg-black text-white rounded-none border-2 border-black hover:bg-white hover:text-black transition-all font-mono"
                >
                  CLOSE
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Index;